// fetchListings.js
// Fetches listings from Firestore with optional filters for category, price, and ordering.
// Returns an array of listing objects with their Firestore IDs.

import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../services/firebase";

/**
 * Fetch listings from Firestore with optional filters.
 * @param {Object} filters - Filtering options: category, minPrice, maxPrice, orderBy
 * @returns {Promise<Array>} Array of listing objects
 */
export default async function fetchListings(filters = {}) {
  // Start with the 'listings' collection
  let q = collection(db, "listings");
  const conditions = [];
  
  // Store price filters for client-side filtering
  const clientFilters = {
    minPrice: null,
    maxPrice: null
  };

  // Filter by sellerId or createdBy (support both field names)
  if (filters.userId) {
    // Query by sellerId first (new field name)
    conditions.push(where("sellerId", "==", filters.userId));
    
    // For seller's own listings, only exclude archived items if requested
    if (filters.excludeArchived) {
      // Note: We'll filter out archived items after fetching to handle null/undefined status
    }
  }
  
  // Filter by category if provided
  if (filters.category) {
    conditions.push(where("category", "==", filters.category));
  }
  
  // Store price filters for client-side filtering (more reliable than Firestore compound queries)
  if (filters.minPrice !== null && filters.minPrice !== undefined && !isNaN(filters.minPrice) && filters.minPrice >= 0) {
    clientFilters.minPrice = filters.minPrice;
  } else {
    }
  
  if (filters.maxPrice !== null && filters.maxPrice !== undefined && !isNaN(filters.maxPrice) && filters.maxPrice >= 0) {
    clientFilters.maxPrice = filters.maxPrice;
  } else {
    }
  
  // Order results (default: newest first)
  // Support both field and direction (e.g., { orderBy: 'price', orderDir: 'asc' })
  if (filters.orderBy) {
    const dir = filters.orderDir || "desc";
    q = query(q, ...conditions, orderBy(filters.orderBy, dir));
  } else {
    q = query(q, ...conditions, orderBy("createdAt", "desc"));
  }

  // Fetch documents and map to array with IDs
  try {
    const snapshot = await getDocs(q);
    let results = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    
    // Apply client-side price filtering
    if (clientFilters.minPrice !== null) {
      results = results.filter(item => item.price >= clientFilters.minPrice);
    }
    
    if (clientFilters.maxPrice !== null) {
      results = results.filter(item => item.price <= clientFilters.maxPrice);
    }
    
    // Apply status filtering after fetching to handle null/undefined status properly
    if (filters.userId && filters.excludeArchived) {
      // For sellers: exclude only archived and deep_archived items
      results = results.filter(item => 
        item.status !== "archived" && item.status !== "deep_archived"
      );
    } else if (!filters.userId) {
      // For public marketplace: exclude sold, archived, and deep_archived items
      results = results.filter(item => 
        item.status !== "sold" && 
        item.status !== "archived" && 
        item.status !== "deep_archived"
      );
    }
    
    // If we got results, return them
    if (results.length > 0 || !filters.userId) {
      return results;
    }
    
    // If no results and we filtered by userId, try querying by createdBy (legacy field)
    const legacyConditions = [];
    legacyConditions.push(where("createdBy", "==", filters.userId));
    
    if (filters.category) {
      legacyConditions.push(where("category", "==", filters.category));
    }
    
    // Don't add price conditions to Firestore query - will filter client-side
    
    let legacyQuery;
    if (filters.orderBy) {
      const dir = filters.orderDir || "desc";
      legacyQuery = query(collection(db, "listings"), ...legacyConditions, orderBy(filters.orderBy, dir));
    } else {
      legacyQuery = query(collection(db, "listings"), ...legacyConditions, orderBy("createdAt", "desc"));
    }
    
    const legacySnapshot = await getDocs(legacyQuery);
    let legacyResults = legacySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    
    // Apply client-side price filtering to legacy results
    if (clientFilters.minPrice !== null) {
      legacyResults = legacyResults.filter(item => item.price >= clientFilters.minPrice);
    }
    
    if (clientFilters.maxPrice !== null) {
      legacyResults = legacyResults.filter(item => item.price <= clientFilters.maxPrice);
    }
    
    // Apply same status filtering for legacy results
    if (filters.userId && filters.excludeArchived) {
      legacyResults = legacyResults.filter(item => 
        item.status !== "archived" && item.status !== "deep_archived"
      );
    } else if (!filters.userId) {
      legacyResults = legacyResults.filter(item => 
        item.status !== "sold" && 
        item.status !== "archived" && 
        item.status !== "deep_archived"
      );
    }
    
    return legacyResults;
    
  } catch (error) {
    if (error.code === 'failed-precondition' && error.message.includes('index')) {
      // Index is still building, return empty array temporarily
      return [];
    }
    throw error;
  }
}
